/**
 * Pure helpers for the SensorGraph Y-axis bounds.
 *
 * Extracted so the axis-floor logic can be unit-tested without instantiating
 * the Chart.js-backed SensorGraph class component.
 *
 * Background (Linear N-23): SensorGraph is shared across several web graphs.
 * Its default suggested Y-axis floor is -10 because the Water Potential graph
 * legitimately plots negative CBar values. But graphs like Accumulated Water
 * Input / Nitrogen / Discharge pass suggestedMinimum = 0 because their data is
 * never negative. In active (non-archive) mode with no irrigation_hin, the
 * component calls resetYAxisBounds(), which restores the shared -10 default and
 * previously clobbered the caller's 0 — making the axis render negative ticks
 * (visible only in imperial/inch, where the small data range let -10 dominate).
 */

// Shared default suggested Y-axis floor. Kept negative so Water Potential's
// CBar range renders; graphs whose data starts at 0 override it via
// suggestedMinimum. Mirrors the value hardcoded in SensorGraph's scale config
// and resetYAxisBounds().
const DEFAULT_SUGGESTED_MIN = -10;

/**
 * Resolve the Y-axis suggestedMin to apply in active mode after
 * resetYAxisBounds() has restored the shared default.
 *
 * A caller-provided suggestedMinimum (e.g. 0 for accumulated water input) wins;
 * callers that pass undefined (e.g. Soil Temperature) keep the -10 default, and
 * Water Potential keeps its own negative suggestedMinWPValue.
 *
 * @param {number|undefined|null} suggestedMinimum caller's requested floor
 * @returns {number} the suggestedMin to set on the Y-axis
 */
const resolveActiveModeSuggestedMin = (suggestedMinimum) =>
  suggestedMinimum != undefined ? suggestedMinimum : DEFAULT_SUGGESTED_MIN;

/**
 * Resolve the HARD Y-axis floor for graphs that must never render below 0
 * (Accumulated Water Input, which passes forceZeroYAxisMin). A soft
 * suggestedMin is not enough: it gets clobbered back to -10 on the reset paths
 * and is still rounded past by Chart.js "nice tick" logic (visible in inch,
 * where the small data range let -10 dominate). A hard `min: 0` is immune to
 * both, so it fixes the axis on every code path.
 *
 * Returns the { min, suggestedMin } to stamp on the y-scale, or null when the
 * graph does not force a zero floor (leave the existing bounds untouched so
 * Water Potential / Soil Temperature / etc. are unaffected).
 *
 * @param {boolean} forceZeroYAxisMin whether this graph pins the axis to 0
 * @returns {{min: number, suggestedMin: number}|null}
 */
const resolveForcedZeroFloor = (forceZeroYAxisMin) =>
  forceZeroYAxisMin ? { min: 0, suggestedMin: 0 } : null;

/**
 * The Y-tick callback blanks the first and last tick labels. When the axis is
 * hard-pinned to a 0 floor, the 0 baseline becomes the first tick and its label
 * would disappear — so keep it. Only applies when forceZeroYAxisMin is set, so
 * other graphs' first/last blanking is unchanged.
 *
 * @param {number} value the tick value
 * @param {boolean} forceZeroYAxisMin whether this graph pins the axis to 0
 * @returns {boolean} true if this tick's label must stay visible
 */
const shouldKeepZeroBaselineTick = (value, forceZeroYAxisMin) =>
  !!forceZeroYAxisMin && value === 0;

module.exports = {
  DEFAULT_SUGGESTED_MIN,
  resolveActiveModeSuggestedMin,
  resolveForcedZeroFloor,
  shouldKeepZeroBaselineTick,
};
